"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Wrench, Gauge, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const specifications = [{
  label: "CHASSIS SPEC",
  value: "Solid milled tubular steel frame",
  icon: ShieldCheck
}, {
  label: "DRIVE SYSTEM",
  value: "Dual-axis electric gearmotors",
  icon: Gauge
}, {
  label: "COMPONENT SUPPORT",
  value: "Direct inventory for wiring & parts",
  icon: Wrench
}];
export default function Story() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="our-story" data-nav="light" className="relative w-full border-b border-border bg-background py-16 md:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16 items-start">
          {/* Left Column: Commercial Narrative */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUpVariant} className="lg:col-span-6 flex flex-col justify-start">
            {/* Datum Hairline Label */}
            <div className="inline-flex items-center gap-2 border border-border bg-muted px-3 py-1 text-xs font-mono tracking-widest text-muted-foreground uppercase self-start mb-6">
              <span className="inline-block size-1.5 bg-primary" />
              Company Background & Standards
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground leading-tight">
              Built for commercial venues, not the weekend party circuit.
            </h1>

            <p className="mt-6 text-base sm:text-lg leading-relaxed text-foreground font-sans">
              Mechanical Bull Shop operates as an industrial machinery supplier.
              We focus on equipment sales, structural parts, and direct technical
              support for bar operators, family entertainment centers, and commercial venues.
            </p>

            <div className="mt-6 space-y-4 text-sm sm:text-base leading-relaxed text-muted-foreground font-sans">
              <p>
                Most bulls in the market are sold through novelty rental brokers with limited documentation
                and zero after-sale parts support. We structured our shop around standard machinery
                principles: verified phase power requirements, published gear ratio outputs, transparent
                individual inspection logs on pre-owned units, and guaranteed stock on replacement seals,
                gearboxes, and controllers.
              </p>
              <p>
                Every unit we ship is calibrated for heavy daily operation, complete with redundant emergency
                stop circuits, reinforced marine-grade enclosures, and clear installation schematics so your
                local electrician can connect and sign off without guesswork.
              </p>
            </div>

            {/* Spec Checklist Block */}
            <div className="mt-8 border-t border-border pt-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {specifications.map((item, index) => {
                const Icon = item.icon;
                return <div key={item.label} data-index={index} className="border border-border bg-muted/40 p-4">
                      <div className="flex items-center gap-2 text-primary">
                        <Icon className="size-4 shrink-0" />
                        <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                          {item.label}
                        </span>
                      </div>
                      <p className="mt-2 text-xs font-medium text-foreground">
                        {item.value}
                      </p>
                    </div>;
              })}
              </div>
            </div>

            {/* Action Links */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-5 font-serif uppercase tracking-wider text-base">
                <Link to="/ContactPage">
                  Talk with equipment specialists
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background px-6 py-5 font-serif uppercase tracking-wider text-base transition-colors">
                <Link to="/NewMechanicalBullsPage">View new bull specifications</Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Industrial Machinery Rig Photo & Plate */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUpVariant} className="lg:col-span-6 flex flex-col gap-6">
            {/* Visual Assembly Box */}
            <div className="relative border border-border bg-muted p-2">
              <div className="overflow-hidden border border-border bg-card">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/industrial_assembly_floor_machinery.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/industrial_assembly_floor_machinery.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/industrial_assembly_floor_machinery.png-wvc-sizes" alt="Industrial commercial mechanical bull chassis on manufacturing assembly floor" loading="lazy" decoding="async" className="w-full h-auto aspect-[4/3] object-cover" />
              </div>

              {/* Machinery Spec Plate Overlay / Sub-panel */}
              <div className="mt-2 border border-border bg-card p-5">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div>
                    <span className="font-mono text-xs uppercase text-muted-foreground tracking-widest block">
                      EQUIPMENT CLASSIFICATION
                    </span>
                    <h2 className="font-serif text-lg font-semibold uppercase text-foreground">
                      MBS Heavy Commercial Rig Series
                    </h2>
                  </div>
                  <span className="border border-border bg-muted px-2 py-1 font-mono text-xs uppercase text-foreground">
                    FACTORY DIRECT
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4 text-xs font-mono">
                  <div>
                    <span className="text-muted-foreground block">FRAME MATERIAL:</span>
                    <span className="text-foreground font-semibold">Milled Tubular Steel (Q235B)</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">SAFETY CIRCUITRY:</span>
                    <span className="text-foreground font-semibold">Dual Dual-Action E-Stops</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">DRIVE RUNTIME:</span>
                    <span className="text-foreground font-semibold">Continuous Duty Rated</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">DISTRIBUTION:</span>
                    <span className="text-foreground font-semibold">Nationwide LTL Freight</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Supplementary Facility Footnote */}
            <div className="border-l-2 border-primary pl-4">
              <p className="text-xs text-muted-foreground font-mono leading-relaxed">
                Operating hours: Monday - Friday: 8am - 6pm; Saturday: 9am - 3pm. All units ship fully crated with comprehensive wiring diagrams, structural stress certifications, and spare wear-part kits.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}